import { type Accessor, type ParentProps, Show, createContext, createSignal, mergeProps, useContext } from "solid-js";
import { useDeeplink } from "./primitives/deeplink";
import { type DeviceType, useDeviceType } from "./primitives/device";
import { DeeplinkProvider } from "./providers/DeeplinkProvider";

interface WidgetSettings {
	theme: string;
	locale: string;
	parent: Document;
	screen: Accessor<DeviceType>;
	renderContainer: Accessor<HTMLDivElement | undefined>;
}

const DEFAULTS = {
	theme: "light",
	locale: "nl-NL",
	parent: document,
} satisfies Partial<WidgetSettings>;

export const SettingsContext = createContext<WidgetSettings>(DEFAULTS as WidgetSettings);

export interface WidgetsProviderProps extends ParentProps {
	settings?: Partial<WidgetSettings>;
}

/**
 * A provider which must be used to wrap all widgets with
 *
 * @param props
 * @returns
 */
export function WidgetsProvider(props: Omit<WidgetsProviderProps, "screen" | "renderContainer">) {
	const [renderContainer, setRenderContainer] = createSignal<HTMLDivElement>();
	const screen = useDeviceType();
	const { initialized } = useDeeplink(renderContainer);

	const settings = mergeProps(DEFAULTS, props.settings, { screen, renderContainer });

	return (
		<SettingsContext.Provider value={settings}>
			<div
				class="yaa-widget-render"
				ref={setRenderContainer}
			>
				<Show when={initialized()}>
					<DeeplinkProvider>{props.children}</DeeplinkProvider>
				</Show>
			</div>
		</SettingsContext.Provider>
	);
}

/**
 * Retrieve the current widget settings state
 */
export function useWidgetApp() {
	return useContext(SettingsContext);
}
